import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, first, Observable, tap } from 'rxjs';
import { PAGE } from '../app.routes';


/**
 * This service handles labels, validation messages and locale.
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {

	private readonly http = inject(HttpClient);

	labels = signal<any>({});

	readonly DEFAULT_LANG = 'en';
	private readonly DEFAULT_LOCALE_ID = 'en-US';

	private _lang: string = this.DEFAULT_LANG;
	private _localeId: string = this.DEFAULT_LOCALE_ID;

	private readonly _isLabelsLoaded$ = new BehaviorSubject<boolean>(false);


	/**
	 * Get current language.
	 */
	get currentLang(): string {
		return this._lang ?? this.DEFAULT_LANG;
	}

	/**
	 * Check if labels are loaded.
	 */
	get isLabelsLoaded(): Observable<boolean> {
		return this._isLabelsLoaded$.asObservable().pipe(first(loaded => !!loaded));
	}

	/**
	 * Load labels.
	 */
	loadLabels(lang: string = this.DEFAULT_LANG): Observable<any> {
		this._lang = lang ?? this.DEFAULT_LANG;
		return this.http.get<any>(`labels/labels-${this._lang}.json`).pipe(
			tap(labels => {
				this.labels.set(labels);
				if (labels) this._isLabelsLoaded$.next(true);
			})
		);
	}

	/**
	 * Get current locale id.
	 */
	get localeId(): string {
		return this._localeId;
	}

	/**
	 * Set locale id and load angular locales js file. 
	 */
	initLocale(): Promise<any> {
		this._localeId = navigator.language ?? this.DEFAULT_LOCALE_ID;
		console.log('Loading locales: ' + this._localeId);
		return Promise.resolve();
	}
}

export async function initLocale(langSrv: LanguageService, router: Router) {
	return langSrv.initLocale().catch(() => router.navigateByUrl(PAGE.ERROR));
}

export function getLocale(langSrv: LanguageService) {
	return langSrv.localeId;
}